import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function AboutUs() {
  const { ref: sectionRef, isVisible } = useScrollAnimation();
  const { ref: imgRef, isVisible: imgVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section
      id="about"
      ref={sectionRef as React.RefObject<HTMLElement>}
      className="py-16 lg:py-20 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div
            className={`order-2 lg:order-1 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="mb-4">
              <span className="inline-block px-3 py-1 bg-white/60 border border-slate-900/10 text-slate-900/70 text-sm font-semibold uppercase tracking-widest rounded">
                Who We Are
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 leading-tight mb-6">
              Responsive, reliable, and ready when you need us.
            </h2>
            <p className="text-lg text-slate-900 leading-relaxed mb-6">
              CJ Solutions Ltd. provides corporate consulting services in Cyprus, specialising in compliance and advisory solutions that reduce complexity and safeguard growth. With deep expertise in regulated industries, we help businesses launch, operate, and expand with confidence.
            </p>
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-amber-500 text-lg font-semibold underline underline-offset-4 hover:text-amber-600 transition-colors cursor-pointer"
            >
              Know About Us
            </a>
          </div>

          {/* Images - Optimized with lazy loading */}
          <div
            ref={imgRef as React.RefObject<HTMLDivElement>}
            className={`order-1 lg:order-2 relative transition-all duration-700 delay-200 ${
              imgVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="relative h-[450px] lg:h-[500px] group">
              <figure className="absolute top-[calc(50%+52px)] -translate-y-1/2 left-0 w-[280px] lg:w-[300px] h-[230px] lg:h-[250px] rounded-2xl overflow-hidden shadow-xl z-10 transition-all duration-500 group-hover:top-[calc(50%-12px)]">
                <div className="absolute inset-0 bg-amber-400/20 mix-blend-multiply z-10"></div>
                <img
                  src="https://public.readdy.ai/ai/img_res/56d01ba7e87817cdb6ce75f798fd60eb.jpg"
                  alt="CJ Solutions team collaboration"
                  width={300}
                  height={250}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </figure>
              <figure className="absolute top-[calc(50%-40px)] -translate-y-1/2 left-[140px] lg:left-[160px] w-[300px] lg:w-[320px] h-[250px] lg:h-[270px] rounded-2xl overflow-hidden shadow-2xl bg-white z-20 transition-all duration-500 group-hover:-translate-x-2">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20interior%20with%20elegant%20desk%20setup%20premium%20notebook%20and%20pen%20on%20clean%20white%20surface%20soft%20natural%20lighting%20professional%20workspace%20aesthetic%20warm%20neutral%20tones%20sophisticated%20business%20environment&width=640&height=540&seq=office1&orientation=portrait"
                  alt="Professional workspace"
                  width={320}
                  height={270}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </figure>
              <figure className="absolute top-[calc(50%+34px)] -translate-y-1/2 left-[220px] lg:left-[240px] w-[280px] lg:w-[300px] h-[230px] lg:h-[250px] rounded-2xl overflow-hidden shadow-2xl bg-white z-30 transition-all duration-500 group-hover:top-[calc(50%+6px)]">
                <img
                  src="https://readdy.ai/api/search-image?query=Confident%20business%20professional%20woman%20smiling%20in%20modern%20corporate%20office%20environment%20wearing%20elegant%20business%20attire%20warm%20natural%20lighting%20authentic%20portrait%20professional%20headshot%20style%20friendly%20approachable&width=600&height=500&seq=professional1&orientation=portrait"
                  alt="CJ Solutions professional"
                  width={300}
                  height={250}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </figure>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
